import {
  ContentCut as CutIcon,
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
  Delete as DeleteIcon,
  LabelImportant as FlowIcon,
} from "@mui/icons-material";
import { Box, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import type { MouseEvent as ReactMouseEvent } from "react";
import { EDITOR_CONTEXT_MENU_Z_INDEX } from "../components/editorPickerPopoverZIndex.js";

const CatalogCreateMenuDot = () => (
  <Box
    component="span"
    sx={{
      width: 20,
      height: 20,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Box
      component="span"
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        bgcolor: "action.active",
      }}
    />
  </Box>
);

export interface BehaviorContextMenuState {
  mouseX: number;
  mouseY: number;
}

export interface BehaviorContextMenuProps {
  menuState: BehaviorContextMenuState | null;
  selectionNodeIds: string[];
  cutLabel: string;
  copyLabel: string;
  pasteLabel: string;
  duplicateLabel: string;
  deleteLabel: string;
  createVariableFromSelectionLabel?: string;
  createFunctionFromSelectionLabel?: string;
  canPaste: boolean;
  onClose: () => void;
  onCut: (nodeIds: string[]) => void;
  onCopy: (nodeIds: string[]) => void;
  onPaste: () => void;
  onDuplicate: (nodeIds: string[]) => void;
  onCreateVariableFromSelection?: (nodeIds: string[]) => void;
  onCreateFunctionFromSelection?: (nodeIds: string[]) => void;
  onDelete: (nodeIds: string[]) => void;
}

/**
 * Runs a context-menu action on pointer down so it still fires when a higher
 * z-index panel overlaps the lower rows of the menu (catalog panel on the right).
 */
const runPointerMenuAction = (
  event: ReactMouseEvent,
  menuState: BehaviorContextMenuState | null,
  action: () => void,
  onClose: () => void,
) => {
  event.preventDefault();
  event.stopPropagation();
  if (!menuState) {
    return;
  }
  action();
  onClose();
};

export const BehaviorContextMenu = ({
  menuState,
  selectionNodeIds,
  cutLabel,
  copyLabel,
  pasteLabel,
  duplicateLabel,
  deleteLabel,
  createVariableFromSelectionLabel,
  createFunctionFromSelectionLabel,
  canPaste,
  onClose,
  onCut,
  onCopy,
  onPaste,
  onDuplicate,
  onCreateVariableFromSelection,
  onCreateFunctionFromSelection,
  onDelete,
}: BehaviorContextMenuProps) => {
  const hasTargetNodes = selectionNodeIds.length > 0;

  return (
    <Menu
      open={menuState != null}
      onClose={onClose}
      disableRestoreFocus
      anchorReference="anchorPosition"
      anchorPosition={
        menuState != null
          ? { top: menuState.mouseY, left: menuState.mouseX }
          : undefined
      }
      slotProps={{
        root: {
          sx: {
            zIndex: EDITOR_CONTEXT_MENU_Z_INDEX,
          },
        },
        paper: {
          sx: {
            zIndex: EDITOR_CONTEXT_MENU_Z_INDEX + 1,
          },
        },
      }}
    >
      <MenuItem
        disabled={!hasTargetNodes}
        onClick={() => {
          if (!menuState) return;
          onCut(selectionNodeIds);
          onClose();
        }}
      >
        <ListItemIcon>
          <CutIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{cutLabel}</ListItemText>
      </MenuItem>
      <MenuItem
        disabled={!hasTargetNodes}
        onClick={() => {
          if (!menuState) return;
          onCopy(selectionNodeIds);
          onClose();
        }}
      >
        <ListItemIcon>
          <CopyIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{copyLabel}</ListItemText>
      </MenuItem>
      <MenuItem
        disabled={!canPaste}
        onClick={() => {
          onPaste();
          onClose();
        }}
      >
        <ListItemIcon>
          <PasteIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{pasteLabel}</ListItemText>
      </MenuItem>
      <MenuItem
        disabled={!hasTargetNodes}
        onClick={() => {
          if (!menuState) return;
          onDuplicate(selectionNodeIds);
          onClose();
        }}
      >
        <ListItemIcon>
          <CopyIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{duplicateLabel}</ListItemText>
      </MenuItem>
      {createVariableFromSelectionLabel && onCreateVariableFromSelection ? (
        <MenuItem
          onMouseDown={(event) => {
            runPointerMenuAction(
              event,
              menuState,
              () => onCreateVariableFromSelection(selectionNodeIds),
              onClose,
            );
          }}
        >
          <ListItemIcon>
            <CatalogCreateMenuDot />
          </ListItemIcon>
          <ListItemText>{createVariableFromSelectionLabel}</ListItemText>
        </MenuItem>
      ) : null}
      {createFunctionFromSelectionLabel && onCreateFunctionFromSelection ? (
        <MenuItem
          onMouseDown={(event) => {
            runPointerMenuAction(
              event,
              menuState,
              () => onCreateFunctionFromSelection(selectionNodeIds),
              onClose,
            );
          }}
        >
          <ListItemIcon>
            <FlowIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{createFunctionFromSelectionLabel}</ListItemText>
        </MenuItem>
      ) : null}
      <MenuItem
        disabled={!hasTargetNodes}
        onClick={() => {
          if (!menuState) return;
          onDelete(selectionNodeIds);
          onClose();
        }}
      >
        <ListItemIcon>
          <DeleteIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{deleteLabel}</ListItemText>
      </MenuItem>
    </Menu>
  );
};
